import { localizeSystemText } from '../../../i18n/messages';
import LocalizedMessage from '../../common/LocalizedMessage';
import React, { useState } from "react";
import PropTypes from "prop-types";
import DateField from "../../common/DateField";
import { isValidDateValue } from "../../common/PolishDateInput";
import { displayDate } from "../../common/datePresentation";
import { getClassTypeLabel, getEnrollmentStatusLabel } from "./enrollmentDisplay";
import { useTranslation } from 'react-i18next';

const DATE_ERROR = "Wpisz poprawną datę w formacie DD.MM.RRRR";

const todayDate = () => new Date().toISOString().slice(0, 10);

const isPackageEnrollment = enrollment => (
  enrollment &&
  (
    enrollment.packageBalance ||
    ["awaitingPayment", "active", "expired", "depleted", "cancelled"].includes(enrollment.status)
  )
);

function calculatePaymentSummary(enrollment, clientLedger) {
  const terms = enrollment.termsSnapshot || enrollment.product || {};
  const plannedAmount = Number(terms.defaultPrice || 0);
  const paidAmount = (clientLedger || [])
    .filter(entry => entry.clientId === enrollment.clientId)
    .filter(entry => entry.type === "PAYMENT")
    .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);

  return {
    plannedAmount,
    paidAmount,
    missingAmount: Math.max(plannedAmount - paidAmount, 0),
  };
}

const RESERVATION_STATUS_LABELS = {
  reserved: "Zarezerwowana",
  consumed: "Wykorzystana",
  released: "Zwolniona",
};

const getStudentName = (studentId, students) => {
  const student = students.find(item => item.id === studentId);
  if (!student) return studentId;
  return `${student.firstName || ""} ${student.lastName || ""}`.trim() || studentId;
};

const getReservationStatusLabel = status => RESERVATION_STATUS_LABELS[status] || status || "-";

const getClassIdFromLessonId = (lessonId = "") => {
  const parts = lessonId.split("-");
  if (parts.length <= 1) return lessonId;
  return parts.slice(0, parts.length - 1).join("-");
};

const getReservationStartTime = (reservation, lessonRegister, schoolClasses) => {
  if (reservation.startTime) return reservation.startTime;
  const register = lessonRegister.find(item => (
    item &&
    (item.lessonId === reservation.lessonId || item.id === reservation.lessonId)
  ));
  if (register && register.lessonDetails && register.lessonDetails.startTime) {
    return register.lessonDetails.startTime;
  }
  const schoolClass = schoolClasses.find(item => item.id === getClassIdFromLessonId(reservation.lessonId));
  return schoolClass && schoolClass.startTime ? schoolClass.startTime : "";
};

const lessonIdentifier = (lessonId = "") => {
  if (!lessonId) return "-";
  if (lessonId.length <= 18) return lessonId;
  return `${lessonId.slice(0, 8)}...${lessonId.slice(-5)}`;
};

function PackageStatusPanel({
  enrollment,
  clientLedger,
  students,
  lessonRegister,
  schoolClasses,
  onActivate,
  onExtendValidity,
  disabled,
}) {
  const { t: tr , i18n } = useTranslation();
  const [activation, setActivation] = useState({ activationDate: todayDate(), note: "" });
  const [extension, setExtension] = useState({ validityEndDate: "", note: "" });

  const [activationError, setActivationError] = useState("");
  const [extensionError, setExtensionError] = useState("");

  if (!isPackageEnrollment(enrollment)) return null;

  const terms = enrollment.termsSnapshot || enrollment.product || {};
  const balance = enrollment.packageBalance || {};
  const canExtend = enrollment.status === "expired" && Number(balance.availableUnits || 0) > 0;
  const paymentSummary = calculatePaymentSummary(enrollment, clientLedger);

  const handleActivationChange = (event) => {
    const { name, value } = event.target;
    setActivation({ ...activation, [name]: value });
    if (name === "activationDate") setActivationError("");
  };

  const handleExtensionChange = (event) => {
    const { name, value } = event.target;
    setExtension({ ...extension, [name]: value });
    if (name === "validityEndDate") setExtensionError("");
  };

  return (
    <section className="mt-3 mb-3">
      <h3 className="manage-title h5">{terms.name || tr("common:package")}</h3>
      <p className="manage-text mb-1">
        <span>{tr("manage:packageStatus_206")} </span>
        <strong>{localizeSystemText(getEnrollmentStatusLabel(enrollment.status), tr)}</strong>
      </p>
      {enrollment.paymentDueDate && (
        <p className="manage-text mb-1">{tr('common:paymentDue', { date: displayDate(enrollment.paymentDueDate, i18n.language) })}</p>
      )}
      {enrollment.validityEndDate && (
        <p className="manage-text mb-1">{tr('common:validUntil', { date: displayDate(enrollment.validityEndDate, i18n.language) })}</p>
      )}
      {enrollment.packageBalance && (
        <div className="manage-text mb-2">
          <span className="mr-3">{`Razem: ${balance.totalUnits}`}</span>
          <span className="mr-3">{`Zuzyte: ${balance.consumedUnits}`}</span>
          <span className="mr-3">{`Zarezerwowane: ${balance.reservedUnits}`}</span>
          <span>{`Dostepne: ${balance.availableUnits}`}</span>
        </div>
      )}
      {(enrollment.packageReservations || []).length > 0 && (
        <section className="mt-3">
          <h3 className="manage-title h5">{tr("manage:lessonsInThePackage_207")}</h3>
          <div className="row table-header manage-table-title text-center mx-0 d-flex align-items-center">
            <div className="col-sm-2 table-cell">{tr("manage:date_8")}</div>
            <div className="col-sm-1 table-cell">{tr("calendar:time")}</div>
            <div className="col-sm-2 table-cell">{tr("manage:student_208")}</div>
            <div className="col-sm-3 table-cell">{tr("manage:lessonType_196")}</div>
            <div className="col-sm-2 table-cell">{tr("manage:status_146")}</div>
            <div className="col-sm-2 table-cell">{tr("manage:lesson_209")}</div>
          </div>
          {[...(enrollment.packageReservations || [])]
            .sort((left, right) => (
              `${left.lessonDate || ""}#${getReservationStartTime(left, lessonRegister, schoolClasses)}#${left.lessonId || ""}`
                .localeCompare(`${right.lessonDate || ""}#${getReservationStartTime(right, lessonRegister, schoolClasses)}#${right.lessonId || ""}`)
            ))
            .map((reservation, index) => {
              const startTime = getReservationStartTime(reservation, lessonRegister, schoolClasses);
              return (
                <div
                  key={reservation.id || `${reservation.lessonId}#${reservation.studentId}`}
                  className={`table-row row d-flex manage-text text-center mx-0 ${index % 2 !== 0 ? "table-row-alternate" : ""}`}
                >
                  <div className="col-sm-2 table-cell">{displayDate(reservation.lessonDate, i18n.language) || "-"}</div>
                  <div className="col-sm-1 table-cell">{startTime || "-"}</div>
                  <div className="col-sm-2 table-cell">{getStudentName(reservation.studentId, students)}</div>
                  <div className="col-sm-3 table-cell">{getClassTypeLabel(reservation.classTypeId)}</div>
                  <div className="col-sm-2 table-cell">{localizeSystemText(getReservationStatusLabel(reservation.status), tr)}</div>
                  <div className="col-sm-2 table-cell" title={reservation.lessonId || ""}>{lessonIdentifier(reservation.lessonId)}</div>
                </div>
              );
            })}
        </section>
      )}

      {enrollment.status === "awaitingPayment" && (
        <section className="mt-3">
          <h3 className="manage-title h5">{tr("manage:activatePackage_210")}</h3>
          <p className="manage-text mb-2">{tr("manage:anOperatorCanActivateThePackageManuallyTheDecision_211")}</p>
          {paymentSummary.missingAmount > 0 && (
            <div className="alert alert-warning">
              {tr('common:missingPayments', { amount: paymentSummary.missingAmount })}
            </div>
          )}
          <div className="form-row">
          <div className="form-group col-md-3">
            <label htmlFor="package-activation-date">{tr("manage:activationDate_212")}</label>
            <DateField
              id="package-activation-date"
              className="form-control"
              type="date"
              name="activationDate"
              value={activation.activationDate}
              onChange={handleActivationChange}
              aria-invalid={!!activationError}
              aria-describedby={activationError ? "package-activation-date-error" : undefined}
            />
            {activationError && <div id="package-activation-date-error" role="alert" className="text-danger"><LocalizedMessage isError value={activationError} /></div>}
          </div>
          <div className="form-group col-md-7">
            <label htmlFor="package-activation-note">{tr("manage:activationNote_213")}</label>
            <input
              id="package-activation-note"
              className="form-control"
              name="note"
              value={activation.note}
              onChange={handleActivationChange}
            />
          </div>
          <div className="form-group col-md-2 d-flex align-items-end">
            <button
              type="button"
              className="btn manage-button manage-red-button btn-block"
              disabled={disabled}
              onClick={() => {
                if (!isValidDateValue(activation.activationDate)) {
                  setActivationError(DATE_ERROR);
                  return;
                }
                onActivate({ ...activation, paymentSummary });
              }}
            >{tr("manage:activatePackage_214")}</button>
          </div>
          </div>
        </section>
      )}

      {canExtend && (
        <div className="form-row">
          <div className="form-group col-md-4">
            <label htmlFor="package-validity-end-date">{tr("manage:newExpiryDate_215")}</label>
            <DateField
              id="package-validity-end-date"
              className="form-control"
              type="date"
              name="validityEndDate"
              value={extension.validityEndDate}
              onChange={handleExtensionChange}
              aria-invalid={!!extensionError}
              aria-describedby={extensionError ? "package-validity-end-date-error" : undefined}
            />
            {extensionError && <div id="package-validity-end-date-error" role="alert" className="text-danger"><LocalizedMessage isError value={extensionError} /></div>}
          </div>
          <div className="form-group col-md-5">
            <label htmlFor="package-extension-note">{tr("manage:extensionNote_216")}</label>
            <input
              id="package-extension-note"
              className="form-control"
              name="note"
              value={extension.note}
              onChange={handleExtensionChange}
            />
          </div>
          <div className="form-group col-md-3 d-flex align-items-end">
            <button
              type="button"
              className="btn manage-button manage-red-button btn-block"
              disabled={disabled}
              onClick={() => {
                if (!isValidDateValue(extension.validityEndDate)) {
                  setExtensionError(DATE_ERROR);
                  return;
                }
                onExtendValidity(extension);
              }}
            >{tr("manage:extendValidity_217")}</button>
          </div>
        </div>
      )}
    </section>
  );
}

PackageStatusPanel.propTypes = {
  enrollment: PropTypes.object.isRequired,
  clientLedger: PropTypes.array,
  students: PropTypes.array,
  lessonRegister: PropTypes.array,
  schoolClasses: PropTypes.array,
  onActivate: PropTypes.func.isRequired,
  onExtendValidity: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

PackageStatusPanel.defaultProps = {
  clientLedger: [],
  students: [],
  lessonRegister: [],
  schoolClasses: [],
  disabled: false,
};

export default PackageStatusPanel;
